import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { LabService } from '../../../../services/lab.service';
import type {
    // ILabChildOrder,
    // ILabOrder,
    ILabResearchChild,
    INlabResponse,
    IResponseGetListResearch,
    IResponseLabOrders,
    TResponseGetListResearch,
} from '../../../../interfaces/lab.interfaces';

import { AlertService } from 'src/lib/ariadna/alert';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { HttpNewService } from 'src/app/services/application/http-new.service';
import { TPrintResponse } from 'src/app/interfaces/lab.interfaces';
import {
    getNameDay,
    getStrEnd,
    getTekDay,
    getTime,
    strToDate,
} from '../../../application/global.function';
import { opendirSync, stat } from 'fs';
import { AbortManagerService } from 'src/app/services/application/abort-manager.service';
import {
    formatUiDate,
    formatUiDateTime,
    toDateTime,
} from 'src/app/shared/date/date.utils';

@Component({
    selector: 'app-order-details',
    templateUrl: './order-details.component.html',
    styleUrls: ['./order-details.component.scss'],
    standalone: false
})
export class OrderDetailsComponent implements OnChanges {
    @Input() public selectedOrder: IResponseLabOrders | null = null;
    @Output() public closeDetailsEvent = new EventEmitter<Event>();
    @Output() public openDocReseachChildModalEvent = new EventEmitter<{
        result: IResponseGetListResearch; //TResponseGetListResearch['researchChildList'][0];
        event: Event;
    }>();

    public loadingResearchList: boolean = false;
    public isMobile: boolean = false;
    public labOrdersChild: Array<IResponseGetListResearch> = [];
    public loadingPdfIds: Set<number> = new Set<number>();

    public constructor(
        private lab: LabService,
        private alert: AlertService,
        private http: HttpNewService,
        private abortManager: AbortManagerService,
    ) {}
    private resizeHandler!: () => void;
    public ngOnInit(): void {
        this.isMobile = window.innerWidth <= 767;

        this.resizeHandler = () => {
            this.isMobile = window.innerWidth <= 767;
        };

        window.addEventListener('resize', this.resizeHandler);
    }

    public ngOnDestroy(): void {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        ['lab_child'].forEach((key) => {
            this.abortManager.reset(key);
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedOrder'] && changes['selectedOrder'].currentValue) {
            this.loadChildResearch(changes['selectedOrder'].currentValue);
        }
    }

    private loadChildResearch(order: IResponseLabOrders): void {
        if (!order || !order.researchId) return;

        this.loadingResearchList = true;

        this.abortManager
            .cancellable(
                'lab_child',
                this.lab.getLaboratoryResearch(order.researchId),
            )
            .subscribe({
                next: (res) => {
                    this.labOrdersChild = res.researchChildList;
                    this.loadingResearchList = false;
                },
                error: (err) => {
                    console.error('Error loading child research:', err);
                    this.loadingResearchList = false;
                },
            });
    }
    public expandedItemId: number | null = null;
    public closingItemId: number | null = null;
    public openDocReseachChildModal(
        result: TResponseGetListResearch['researchChildList'][0],
        event: Event,
    ): void {
        event.stopPropagation();

        if (this.isMobile) {
            // В мобильной версии переключаем раскрытие
            this.expandedItemId =
                this.expandedItemId === result.researchId
                    ? null
                    : result.researchId;
        } else {
            // В десктопной версии открываем модальное окно
            this.openDocReseachChildModalEvent.emit({ result, event });
        }
    }

    public isItemExpanded(researchId: number): boolean {
        return this.expandedItemId === researchId;
    }

    public closeExpandedItem(): void {
        this.expandedItemId = null;
    }

    public isNlabResponse(response: TPrintResponse): response is INlabResponse {
        return 'reportList' in response && Array.isArray(response.reportList);
    }

    public async goPdf(
        researchId: number | undefined,
        e: Event,
        ext: number | undefined,
        int: number | undefined,
    ) {
        e.preventDefault();
        e.stopPropagation();
        if (!researchId) return;

        const type: string = int === 1 ? 'int' : 'ext';

        this.loadingPdfIds.add(researchId);

        try {
            const res = await lastValueFrom(
                this.http.get<TPrintResponse>(
                    this.getDownloadUrlForPDF(researchId, type),
                    null,
                    false,
                ),
            );

            if (res && this.isNlabResponse(res)) {
                const base64 = res.reportList[0].pdf;
                this.printNlab(base64); // Обновлённый метод
                return;
            }

            if (res && !this.isNlabResponse(res)) {
                const arrayBuffer = res.response.data as ArrayBuffer;
                const bytes = new Uint8Array(arrayBuffer);

                const file = new File([bytes], `research_${researchId}.pdf`, {
                    type: 'application/force-download',
                });

                const fileURL = URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = fileURL;

                link.rel = 'noopener noreferrer';
                link.download = `research_${researchId}.pdf`;

                document.body.appendChild(link);
                link.click();
                //document.body.removeChild(link);

                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileURL);
                }, 60_000);
            }
        } catch (error) {
            console.error(error);
            this.alert.error('Ошибка загрузки');
        } finally {
            this.loadingPdfIds.delete(researchId);
        }
    }

    public isPdfLoading(researchId: number): boolean {
        return this.loadingPdfIds.has(researchId);
    }
    public getDownloadUrlForPDF(researchId: number | undefined, type: string) {
        return `/labs/pdf/${researchId}.pdf?type=${type}`;
    }

    private printNlab(base64: string): void {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const file = new Blob([bytes.buffer], {
            type: 'application/pdf',
        });
        const fileURL = URL.createObjectURL(file);

        window.location.href = fileURL;

        setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    }

    public getStatusClass(status: string): string {
        const normalizedStatus = status
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '');
        const statusMap: Record<string, string> = {
            выполнен: 'status-completed',
            ожидаетобработки: 'status-pending',
            отменён: 'status-cancelled',
            вработе: 'status-in-progress',
        };
        return statusMap[normalizedStatus] || 'status-default';
    }

    public getCapitalizedStatus(status: string): string {
        if (!status) return '';
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
    public getStatusIndicator(status: string): string {
        if (!status) return '';

        const statusIndicators: Record<string, string> = {
            выполнен: './assets/icons/status/lab-ready.svg',
            'ожидает обработки': './assets/icons/status/lab-in-progress.svg',
            отменён: './assets/icons/status/lab-cancelled.svg',
            'в работе': './assets/icons/status/lab-in-progress.svg',
        };

        return statusIndicators[status.toLowerCase().trim()] || '';
    }

    public getStatusClassIcon(status: string): string {
        if (!status || typeof status !== 'string') return '';

        const normalizedStatus = status
            .replace(/[\s\u00A0\u2000-\u200B\u2028-\u2029\u3000]/g, '')
            .toLowerCase();

        if (normalizedStatus === 'ожидаетобработки') {
            return 'icon-background__inwork';
        }
        if (normalizedStatus === 'отменён') {
            return 'icon-background__abort';
        }
        if (normalizedStatus === 'вработе') {
            return 'icon-background__inwork';
        }
        return '';
    }

    public getDateOrder(date: Date | string | undefined): string {
        if (!date) return '';
        return formatUiDate(new Date(date));
    }

    public strEnd(
        val: number | undefined,
        str: string,
        massStr: string[],
    ): string {
        return getStrEnd(val, str, massStr);
    }
    public closeDetails(event?: Event): void {
        this.closeDetailsEvent.emit(event);
    }

    public getTekDay(dt: Date | null | undefined): string {
        if (dt) {
            return getTekDay(dt);
        } else {
            return '!';
        }
    }

    public getNameDay(dt: Date): string {
        return getNameDay(dt);
    }

    public getTime(dt: Date): string {
        return getTime(dt);
    }

    public formatFullDate(dateString: string | undefined): string {
        if (!dateString) return '';

        return formatUiDateTime(strToDate(dateString));
    }

    public getDay(dateString: string | undefined): string {
        if (!dateString) return '';
        return toDateTime(strToDate(dateString)).toFormat('cccc');
    }
}
